var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Timeouts } from './framework/Timeouts';
var Model;
(function (Model) {
    Model["viewSonic"] = "-LvpZqFPdm8NxNkwSCF4";
    Model["benQ"] = "-M0NrOOPayqgfHudnNUK";
})(Model || (Model = {}));
export class ViewSonicViewBoard {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.deviceId = '01'; // 99 is a broadcast to all displays
        this.isMacAddressSpecified = false;
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        this.model = Model.viewSonic;
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`8${this.deviceId}gl000`, /8\d{2}rl00(\d)/i, tags);
                return match[1] === '1';
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                // Clear any pending busy timeouts that would set busy back to false. The power set
                // methods will set busy to true and manage the transition back to false.
                this.busyTimeout.clear();
                if (this.isMacAddressSpecified) {
                    if (this.socket.options.allowNoConnection) {
                        return this.setPowerWakeOnLanNetwork(power, tags);
                    }
                }
                return this.setPowerSimple(power, tags);
            }),
            powerOffRestrictions: (config) => {
                return this.socket.state === 'connected' ? [] : ['get'];
            },
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`8${this.deviceId}gj000`, /8\d{2}rj([0-9a-f]{3})/i, tags);
                let videoInputInt = parseInt(match[1], 16);
                // BenQ video inputs can have 3 digits, but not ViewSonic
                if (this.model === Model.viewSonic && videoInputInt >= 0x100) {
                    videoInputInt -= 0x100; // ignore signal ready byte for viewsonic
                }
                return videoInputInt.toString(16).toUpperCase(); // remove leading 0's
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                const input = code.padStart(3, '0');
                const match = yield this.socket.send(`8${this.deviceId}s\x22${input}`, /4\d{2}([+-])/i, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                return match[1] === '+' ? code : this.state.videoInput.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`8${this.deviceId}gf000`, /8\d{2}rf(\d+)/i, tags);
                return parseInt(match[1], 10);
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const vol = volume.toString().padStart(3, '0');
                const match = yield this.socket.send(`8${this.deviceId}s5${vol}`, /4\d{2}([+-])/i, tags);
                return match[1] === '+' ? volume : this.state.volume.value;
            }),
            powerOffRestrictions: ['get', 'set'],
            range: { max: 100, min: 0, round: 1 },
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`8${this.deviceId}gg000`, /8\d{2}rg00(\d)/i, tags);
                return match[1] === '1';
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`8${this.deviceId}s600${mute ? '1' : '0'}`, /4\d{2}([+-])/i, tags);
                return match[1] === '+' ? mute : this.state.volumeMute.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            { intervalMs: 2500, controls: ['power', 'videoInput'] },
            {
                intervalMs: 5000,
                pollWhenDisconnected: true,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (!this.state.power.value &&
                        this.socket.options.allowNoConnection &&
                        this.socket.getState() !== 'connected') {
                        if (!this.framework.isReconnecting)
                            yield this.framework.reconnect();
                    }
                }),
            },
        ];
        if (this.socket.options.allowNoConnection) {
            // If the display is powered off externally (e.g., IR remote or third party controller),
            // then we will lose the socket connection. If this happens, we update the power state
            // to false.
            this.socket.onConnectionLost(() => {
                this.state.power.update(false, true);
            });
        }
    }
    // phase callbacks
    onConfigure(config) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.state.busy.update(false);
            this.model = (_a = config === null || config === void 0 ? void 0 : config.modelNumber) !== null && _a !== void 0 ? _a : Model.viewSonic;
            this.isMacAddressSpecified = config.macAddress !== undefined;
            if (this.isMacAddressSpecified && config.port === undefined) {
                this.log.error(`mac address configured for wake on lan, but no port specified`);
            }
            // Regardless of connection we can't query the power if the device is powered off. The power
            // is initialized to false, but we need to query it if we can when we first connect.
            if (this.socket.state === 'connected') {
                this.state.power.update(yield this.power.get(['power.get']));
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    // other class methods
    setPowerSimple(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(power ? 25000 : 15000);
            this.socket.removeCommands('poll');
            const match = yield this.socket.send(`8${this.deviceId}s!00${power ? '1' : '0'}`, /4\d{2}([+-])/i, tags);
            this.state.busy.update(true);
            const newPower = match[1] === '+' ? power : this.state.power.value;
            this.state.power.update(newPower);
            yield this.timeouts.delay(newPower ? 25000 : 15000);
            this.state.busy.update(false);
            return this.state.power.value;
        });
    }
    setPowerWakeOnLanNetwork(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(power ? 50000 : 25000);
            this.socket.removeCommands('poll');
            if (power) {
                // Send a power command without an expectation of receiving a response.
                if (this.socket.state === 'connected') {
                    yield this.socket.sendAsync('8${this.deviceId}s!001'); // power on
                }
                // For direct Ethernet connections where the socket connection is lost when the display
                // is powered off, we first uppdate the power state so the UI will reflect that the
                // power is on, then we block while sending a wake on lan request and attempting to
                // reconnect to the display.
                this.state.busy.update(true);
                // Update power with a short delay so that framework.reconnect gets called first. This
                // tells StateBridge we are disconnected so it doesn't change phase back to configuring
                // to query the controls with power off restrictions when it sees that the power is
                // back on.
                this.timeouts.set(() => this.state.power.update(power), 100);
                yield this.framework.reconnect({
                    attemptDelayMs: 10000,
                    initialDelayMs: 0,
                    numAttempts: 8,
                    onBeforeReconnect: () => __awaiter(this, void 0, void 0, function* () {
                        return this.framework.sendWakeOnLan();
                    }),
                });
                // When updating the busy state, we must use notifyIfUnchanged = true. This is because
                // the driver reconnects as part of powering on, and reconnecting resets all the states.
                // Without notifyIfUnchanged = true, the busy = false notification won't get sent.
                this.state.busy.update(false, true);
                return this.socket.state === 'connected' ? this.power.get(tags) : false;
            }
            else {
                const match = yield this.socket.send(`8${this.deviceId}s!00${power ? '1' : '0'}`, /4\d{2}([+-])/i, tags);
                this.state.busy.update(true);
                const newPower = match[1] === '+' ? power : this.state.power.value;
                this.state.power.update(newPower);
                yield this.timeouts.delay(15000);
                this.state.busy.update(false);
                return this.state.power.value;
            }
        });
    }
}
// static properties
ViewSonicViewBoard.driverType = 'socketDriver';
ViewSonicViewBoard.key = '-LvpZqFPdm8NxNkwSCF4';
ViewSonicViewBoard.socketOptions = (config) => {
    const baseSocketOptions = {
        commandExtractor: {
            terminators: ['\r'],
        },
        sendTerminator: '\r',
    };
    const hasMacAddress = config.macAddress !== undefined;
    if (config.port === undefined) {
        return [
            Object.assign(Object.assign({}, baseSocketOptions), { port: 5000, allowNoConnection: hasMacAddress, idleTimeoutMs: hasMacAddress ? 0 : 40000 }),
            Object.assign(Object.assign({}, baseSocketOptions), { port: 4660, allowNoConnection: hasMacAddress, idleTimeoutMs: hasMacAddress ? 0 : 40000 }),
            Object.assign(Object.assign({}, baseSocketOptions), { port: 4999, idleTimeoutMs: hasMacAddress ? 0 : 40000, allowNoConnection: false }),
        ];
    }
    // Assume port 4660 and 5000 are direct Ethernet to the device and anything else is via an Ethernet
    // to serial converter.
    return [
        Object.assign(Object.assign({}, baseSocketOptions), { port: config.port, idleTimeoutMs: hasMacAddress && config.port !== 4660 && config.port !== 5000 ? 0 : 40000, allowNoConnection: hasMacAddress && (config.port === 4660 || config.port === 5000) }),
    ];
};
